import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import type { GitRefResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { canCommitTo } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import { repository } from "~/clientInstance";
import type { GetCommitButtonProps } from "./CommitButton";
import { GetCommitButton } from "./CommitButton";
import type { CommitMessageWithDetails } from "./CommitMessageWithDetails";
export function useCommitButton(isGit: boolean, project: ProjectResource, gitRef: GitRefResource | undefined, resourceName: string, onSaveToNewBranch: (newBranch: GitRefResource) => Promise<void>): [
    ((action: {
        isDisabled: boolean;
        onClick: () => Promise<boolean>;
    }) => PrimaryPageAction) | undefined,
    CommitMessageWithDetails
] {
    const emptyCommitMessage = { summary: "", details: "" };
    const [commitMessage, setCommitMessage] = useState<CommitMessageWithDetails>(emptyCommitMessage);
    if (isGit) {
        if (!gitRef) {
            throw new Error("Cannot render commit buttons without a Git reference");
        }
        const commitAction = GetGitSavePrimaryAction(project, gitRef, resourceName, onSaveToNewBranch, commitMessage, setCommitMessage);
        return [commitAction, commitMessage];
    }
    return [undefined, emptyCommitMessage];
}
function GetGitSavePrimaryAction(project: ProjectResource, gitRef: GitRefResource, resourceName: string, onSaveToNewBranch: (newBranch: GitRefResource) => Promise<void>, commitMessage: CommitMessageWithDetails, setCommitMessage: (commitMessage: CommitMessageWithDetails) => void): (action: {
    isDisabled: boolean;
    onClick: () => Promise<boolean>;
}) => PrimaryPageAction {
    async function createNewBranch(branchName: string) {
        const branch = await repository.Branches.createBranch(project, branchName, gitRef?.Name);
        onSaveToNewBranch(branch);
    }
    return ({ isDisabled, onClick }) => {
        const commitButtonProps: GetCommitButtonProps = {
            project,
            gitRef: gitRef?.CanonicalName,
            canCommitToGitRef: canCommitTo(gitRef),
            defaultCommitMessage: `Update ${resourceName}`,
            commitMessage: commitMessage,
            updateCommitMessage: setCommitMessage,
            commitMessageAccessibleName: `Commit message for saving ${resourceName}`,
            commitDetailsAccessibleName: `Commit details for saving ${resourceName}`,
            commitButtonAccessibleName: `Commit changes to ${resourceName}`,
            onNewBranchCreating: createNewBranch,
            label: "Commit",
            busyLabel: "Committing",
            onClick: () => onClick(),
            disabled: isDisabled,
        };
        return {
            type: "custom",
            content: <GetCommitButton {...commitButtonProps}/>,
            key: "Get Commit",
        };
    };
}
